import { HardwareService, type PrintApiJob, PrintApiReceiptPrinterService } from '@dutchie/capacitor-hardware';
import { useMutation } from '@tanstack/react-query';

import { useActiveCustomer } from 'src/queries/curbside/use-carts';
import { errorNotification, successNotification } from 'src/store/actions/notifications-actions';
import { useAppDispatch, useAppSelector } from 'src/utils';
import { post } from 'src/utils/helpers/http';
import { usePrintWorker } from 'src/utils/hooks/printing/usePrintWorker';
import { useBaseAPIParams } from 'src/utils/hooks/useBaseAPIParams';
import { customEventKeys, logger } from 'src/utils/logger';
import type { PrintJobContext, PrintJobResultContext } from 'src/utils/logger/types/printing';

const PRINT_RECEIPT_URL = '/v2/print-jobs/print-receipt';
const CREATE_RECEIPT_JOB_URL = '/v2/print-jobs/create-receipt-job';

type usePrintReceiptType = {
  Subtotal: number;
  Total: number;
};

export function usePrintReceipt({ Subtotal, Total }: usePrintReceiptType) {
  const dispatch = useAppDispatch();
  const baseAPIParams = useBaseAPIParams();

  const { data: activeCustomer } = useActiveCustomer();
  const selectedRegister = useAppSelector((state) => state.user.selectedRegister);
  const { isPrintWorkerEnabled } = usePrintWorker();

  return useMutation({
    mutationFn: async () => {
      if (isPrintWorkerEnabled) {
        const printerId = selectedRegister?.DefaultReceiptPrinterGuid;
        if (!printerId) {
          throw new Error('No receipt printer configured');
        }

        const shipmentId = parseInt(activeCustomer?.ShipmentId ?? '', 10);
        if (isNaN(shipmentId)) {
          throw new Error('No shipment ID available for receipt printing');
        }

        const requestParams = {
          ...baseAPIParams,
          ReceiptType: 'Receipt',
          ShipmentId: shipmentId,
          PrinterId: printerId,
          PopCashDrawer: false,
        };

        const logData = { job: 'receipt' as const, printerId, requestParams };

        logger.info<PrintJobContext>('print receipt started', {
          key: customEventKeys.printing.jobStarted,
          ...logData,
        });

        const job = await post<PrintApiJob>(CREATE_RECEIPT_JOB_URL, requestParams);

        let success = false;
        try {
          success = !!job;
        } finally {
          logger.info<PrintJobResultContext>('print receipt result', {
            key: customEventKeys.printing.jobEnded,
            success,
            ...logData,
          });
        }
      } else {
        await post(PRINT_RECEIPT_URL, {
          ...baseAPIParams,
          Total,
          Subtotal,
          ReceiptType: 'Receipt',
          ReceiptParameters: activeCustomer?.ShipmentId,
          PrinterId: selectedRegister?.ReceiptPrinterId,
          ForDelivery: true,
          localPrinter: false,
        });
      }
    },
    onSuccess: () => dispatch(successNotification('Receipt printed')),
    onError: (e) => {
      logger.error(e, { message: 'Failed to print receipt' });
      dispatch(errorNotification('Receipt failed to print'));
    },
  });
}
